/**
 * One search from anywhere in Quality (case round, finding 7): an item or
 * case by number, customer, order or lot, across Triage, Cases, Routed away
 * and Done, using the lists the shell already loads. Same word match as the
 * Done tab's search.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';
import { isRoutedToUser } from '../../components/portal/threadTriageView';
import { caseMatchesSearch, threadMatchesSearch } from './qualityTriageView';
import { primaryRef } from './caseIdentity';
import { ORIGIN_LABEL, threadOrigin, type WorkspaceOrigin } from './qualityWorkspaceView';

export interface SearchHit {
  key: string;
  no: string;
  title: string;
  /** Which tab it lives on. */
  where: string;
  origin: WorkspaceOrigin;
  /** Path relative to the door. */
  path: string;
  /** Every number the item goes by (the case's thread too), so typing
   *  QT-10054 ranks INV-00006 first (proposal 6). */
  nos: string[];
}

/** Up to `limit` hits; an exact number first, then live items before closed. */
export function quickSearch(
  query: string,
  threads: DoorThreadRead[],
  investigations: DoorInvestigationRead[],
  userName: string | null | undefined,
  limit = 8,
): SearchHit[] {
  const q = query.trim();
  if (q.length < 2) return [];
  const upper = q.toUpperCase();
  // A thread promoted into a case shows once, as the case.
  const caseIds = new Set(investigations.map((i) => i.id));
  const threadHits: SearchHit[] = threads
    .filter((t) => !(t.investigation_id && caseIds.has(t.investigation_id)))
    .filter((t) => threadMatchesSearch(t, q))
    .map((t) => {
      const origin = threadOrigin(t, isRoutedToUser(t, userName));
      return {
        key: `t:${t.id}`,
        no: primaryRef(t, null).label,
        nos: [t.thread_no],
        title: t.title,
        where: ORIGIN_LABEL[origin],
        origin,
        path: `threads/${encodeURIComponent(t.thread_no)}`,
      };
    });
  const caseHits: SearchHit[] = investigations
    .filter((i) => i.status !== 'cancelled')
    .filter((i) => {
      const thread = threads.find((t) => t.investigation_id === i.id);
      return caseMatchesSearch(i, q) || (!!thread && threadMatchesSearch(thread, q));
    })
    .map((i) => {
      const thread = threads.find((t) => t.investigation_id === i.id) ?? null;
      const origin: WorkspaceOrigin = i.status === 'completed' ? 'done' : 'cases';
      return {
        key: `c:${i.id}`,
        no: primaryRef(thread, i).label,
        nos: [i.investigation_no, ...(thread ? [thread.thread_no] : [])],
        title: i.title,
        where: ORIGIN_LABEL[origin],
        origin,
        path: `cases/${encodeURIComponent(i.investigation_no)}`,
      };
    });
  const rank = (h: SearchHit) => (h.nos.some((n) => n.toUpperCase() === upper) ? 0 : h.origin === 'done' ? 2 : 1);
  return [...threadHits, ...caseHits]
    .map((h, i) => ({ h, i }))
    .sort((a, b) => rank(a.h) - rank(b.h) || a.i - b.i)
    .map(({ h }) => h)
    .slice(0, limit);
}
